import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { FileCheck2, FileText, FileType2, Trash2, Eye, Loader2, Plane, Briefcase, Send, Share2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BLANK = {
  employee_id: '', purpose_type: 'travel', salutation: 'Mr.', employee_name: '',
  nationality: '', passport_no: '', designation: '', date_of_joining: '', gross_salary: 0,
  salary_type: 'Gross Salary', visa_validity: '',
  destination_country: '', travel_from: '', travel_to: '', embassy: '', recipient_city: 'Dubai, U.A.E.',
  addressed_to: 'To Whomsoever It May Concern', purpose: '',
  company: 'stars_n_bars', signatory_name: 'Jubu Thankachen', signatory_designation: 'Group H.R. Manager',
  signatory_address: '38, Sky Tower, Reem Island, Abu Dhabi, UAE',
  signatory_phone: '+971 50 7173757 / Office: +971 2 5650104', signatory_email: 'hr@starsnbars.ae',
  subject: 'Re: No Objection Letter', issue_date: '',
};

const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const NOCCertificate = () => {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState('');
  const [year, setYear] = useState('all');
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pendingReqId, setPendingReqId] = useState(null);

  const loadRequests = () => {
    axios.get(`${API}/api/document-requests?status=pending`)
      .then(r => setRequests((r.data || []).filter(x => x.request_type === 'noc_certificate')))
      .catch(() => {});
  };
  useEffect(() => { loadRequests(); }, []);

  const applyRequest = (req) => {
    const d = req.noc_details || {};
    if (req.employee_id) pickEmployee(req.employee_id);
    setForm(p => ({
      ...p,
      employee_id: req.employee_id || p.employee_id,
      employee_name: p.employee_name || req.employee_name || '',
      purpose_type: d.purpose_type === 'work_other' ? 'work_other' : 'travel',
      destination_country: d.country || '',
      travel_from: d.travel_from || '',
      travel_to: d.travel_to || '',
      embassy: d.embassy || '',
      addressed_to: d.addressed_to || d.purpose ? (d.addressed_to || 'To Whomsoever It May Concern') : p.addressed_to,
      purpose: d.purpose || '',
    }));
    setPendingReqId(req.id);
    toast({ title: 'Loaded request', description: `Review & issue the NOC for ${req.employee_name}.` });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (year && year !== 'all') params.set('year', year);
    axios.get(`${API}/api/noc-certificates?${params.toString()}`).then(r => setList(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, year]);
  useEffect(() => { axios.get(`${API}/api/employees`).then(r => setEmployees(r.data || [])).catch(() => {}); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const pickEmployee = (id) => {
    const e = employees.find(x => x.id === id);
    if (!e) return;
    const docs = e.documents || {};
    const sal = e.salary || {};
    const gross = (sal.basic || 0) + (sal.housing || 0) + (sal.transportation || 0) + (sal.other || 0);
    setForm(p => ({
      ...p, employee_id: e.id, salutation: e.gender === 'Female' ? 'Ms.' : 'Mr.',
      employee_name: e.name || '', nationality: e.nationality || '',
      passport_no: docs.passport_number || '', designation: e.position || '',
      date_of_joining: e.join_date || '', gross_salary: gross,
      visa_validity: docs.visa_expiry || docs.visa_expiry_date || '',
    }));
  };

  const years = useMemo(() => {
    const ys = new Set(list.map(n => (n.issue_date || '').slice(0, 4)).filter(Boolean));
    const now = new Date().getFullYear();
    [now, now - 1, now - 2].forEach(y => ys.add(String(y)));
    return Array.from(ys).sort().reverse();
  }, [list]);

  const validate = () => {
    if (!form.employee_name.trim()) return 'Employee name is required';
    if (!form.designation.trim()) return 'Designation is required';
    if (form.purpose_type === 'travel') {
      if (!form.destination_country.trim()) return 'Destination country is required';
      if (!form.embassy.trim()) return 'Embassy / recipient is required';
    } else {
      if (!form.addressed_to.trim()) return 'Addressee is required';
      if (!form.purpose.trim()) return 'Purpose is required';
    }
    return null;
  };

  const issue = async () => {
    const err = validate();
    if (err) { toast({ title: err, variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/noc-certificates`, { ...form, gross_salary: parseFloat(form.gross_salary || 0) });
      const created = res.data;
      // If issued from a portal request, share to the employee + close the request.
      if (pendingReqId && created?.id) {
        try { await axios.post(`${API}/api/noc-certificates/${created.id}/share`); } catch { /* unlinked */ }
        try { await axios.patch(`${API}/api/document-requests/${pendingReqId}`, { status: 'issued', certificate_id: created.id, actioned_by: 'HR/Admin' }); } catch { /* noop */ }
        setPendingReqId(null);
        loadRequests();
      }
      toast({ title: 'NOC issued', description: pendingReqId ? 'Issued & shared to the employee.' : 'Serial number assigned. Download Word or PDF below.' });
      setForm(BLANK); load();
    } catch (e) { toast({ title: 'Could not issue NOC', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const share = async (n) => {
    try {
      await axios.post(`${API}/api/noc-certificates/${n.id}/share`);
      toast({ title: 'Sent to employee', description: 'Now visible under their My Letters.' });
      load();
    } catch (e) { toast({ title: 'Could not share', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const preview = async () => {
    const err = validate();
    if (err) { toast({ title: err, variant: 'destructive' }); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/api/noc-certificates/preview`, { ...form, gross_salary: parseFloat(form.gross_salary || 0) }, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch { toast({ title: 'Preview failed', variant: 'destructive' }); }
    finally { setPreviewing(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this NOC?')) return;
    try { await axios.delete(`${API}/api/noc-certificates/${id}`); load(); toast({ title: 'Deleted' }); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const fieldRow = (label, k, type = 'text') => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={form[k]} onChange={e => set(k, e.target.value)} className="mt-1 h-9" data-testid={`noc-${k}`} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="noc-certificate-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileCheck2 className="h-6 w-6 text-teal-600" /> NOC Certificates</h1>
        <p className="text-gray-600 mt-1">Issue No Objection Certificates — editable fields, serial number, downloadable in Word &amp; PDF.</p>
      </div>

      {requests.length > 0 && (
        <Card className="border-teal-200 bg-teal-50/50" data-testid="noc-requests-queue">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-teal-600" /> Employee NOC Requests ({requests.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {requests.map(req => {
              const d = req.noc_details || {};
              return (
                <div key={req.id} className="flex items-start justify-between gap-2 bg-white border rounded-lg p-3" data-testid={`noc-request-${req.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{req.employee_name} <span className="text-gray-400 font-normal">· {req.employee_code}</span></p>
                    <p className="text-[11px] text-gray-500">
                      {d.purpose_type === 'work_other'
                        ? `Work/Other · ${d.purpose || ''} · to ${d.addressed_to || '—'}`
                        : `Travel · ${d.country || ''} · ${d.travel_from || ''}→${d.travel_to || ''} · ${d.embassy || ''}`}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Requested {fmt(req.created_at)}</p>
                  </div>
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700 shrink-0" onClick={() => applyRequest(req)} data-testid={`noc-request-issue-${req.id}`}>Issue</Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Issue form */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">{pendingReqId ? 'Issue NOC (from request)' : 'Issue New NOC'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Pick Employee (auto-fill)</Label>
              <Select value={form.employee_id} onValueChange={pickEmployee}>
                <SelectTrigger className="mt-1 h-9" data-testid="noc-employee-select"><SelectValue placeholder="Select employee…" /></SelectTrigger>
                <SelectContent>
                  {[...employees].sort((a, b) => (a.employee_code || '').localeCompare(b.employee_code || '')).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <button onClick={() => set('purpose_type', 'travel')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium ${form.purpose_type === 'travel' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600'}`} data-testid="noc-type-travel">
                <Plane className="h-4 w-4" /> Travel
              </button>
              <button onClick={() => set('purpose_type', 'work_other')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium ${form.purpose_type === 'work_other' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600'}`} data-testid="noc-type-work">
                <Briefcase className="h-4 w-4" /> Work / Other
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Title</Label>
                <Select value={form.salutation} onValueChange={v => set('salutation', v)}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{['Mr.', 'Ms.', 'Mrs.', 'Mx.'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">{fieldRow('Employee Name', 'employee_name')}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {fieldRow('Nationality', 'nationality')}
              {fieldRow('Passport No.', 'passport_no')}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {fieldRow('Designation', 'designation')}
              {fieldRow('Date of Joining', 'date_of_joining', 'date')}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {fieldRow('Gross Salary (AED)', 'gross_salary', 'number')}
              {fieldRow('Salary Type', 'salary_type')}
            </div>
            {fieldRow('Visa Valid Until', 'visa_validity', 'date')}

            {form.purpose_type === 'travel' ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {fieldRow('Country to Visit', 'destination_country')}
                  {fieldRow('Embassy (addressed to)', 'embassy')}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {fieldRow('Travel From', 'travel_from', 'date')}
                  {fieldRow('Travel Till', 'travel_to', 'date')}
                </div>
                {fieldRow('Recipient City', 'recipient_city')}
              </>
            ) : (
              <>
                {fieldRow('Addressed To', 'addressed_to')}
                <div>
                  <Label className="text-xs">Purpose of NOC</Label>
                  <Textarea value={form.purpose} onChange={e => set('purpose', e.target.value)} rows={2} className="mt-1" data-testid="noc-purpose" />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              {fieldRow('Signatory Name', 'signatory_name')}
              {fieldRow('Signatory Title', 'signatory_designation')}
            </div>
            {fieldRow('Signatory Address', 'signatory_address')}
            <div className="grid grid-cols-2 gap-2">
              {fieldRow('Phone', 'signatory_phone')}
              {fieldRow('Email', 'signatory_email')}
            </div>
            {fieldRow('Issue Date (blank = today)', 'issue_date', 'date')}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={preview} disabled={previewing} className="flex-1" data-testid="noc-preview-btn">
                {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Preview
              </Button>
              <Button onClick={issue} disabled={saving} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="noc-issue-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileCheck2 className="h-4 w-4 mr-1" />} Issue NOC
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Issued list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Issued NOCs ({list.length})</CardTitle>
            <div className="flex gap-2 mt-2">
              <Input placeholder="Search by employee name…" value={q} onChange={e => setQ(e.target.value)} className="h-9" data-testid="noc-search" />
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="h-9 w-32" data-testid="noc-year-filter"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {list.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No NOCs found.</p>
            ) : (
              <div className="space-y-2">
                {list.map(n => (
                  <div key={n.id} className="border rounded-lg p-3 bg-gray-50" data-testid={`noc-row-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{n.employee_name}</p>
                        <p className="text-[11px] text-gray-500 font-mono">{n.serial_number}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={n.purpose_type === 'travel' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}>
                            {n.purpose_type === 'travel' ? `Travel · ${n.destination_country}` : 'Work / Other'}
                          </Badge>
                          <span className="text-[11px] text-gray-500">{fmt(n.issue_date)}</span>
                          {n.shared_with_employee && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" />Shared</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {n.employee_id && !n.shared_with_employee && (
                          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => share(n)} title="Send to employee (My Letters)" data-testid={`noc-share-${n.id}`}>
                            <Share2 className="h-4 w-4 text-teal-600" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => window.open(`${API}/api/noc-certificates/${n.id}/pdf`, '_blank')} title="PDF" data-testid={`noc-pdf-${n.id}`}>
                          <FileText className="h-4 w-4 text-rose-600" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => window.open(`${API}/api/noc-certificates/${n.id}/docx`, '_blank')} title="Word" data-testid={`noc-docx-${n.id}`}>
                          <FileType2 className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => del(n.id)} title="Delete" data-testid={`noc-delete-${n.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NOCCertificate;
